/**
 * Returns the avatar label for a player.
 * - Bots: "B1", "B2", "B3" (number extracted from name)
 * - Humans: first letter of name uppercased
 */
export function playerInitial(player) {
  if (!player) return "?";
  if (player.is_bot) {
    const num = player.name?.match(/\d+/)?.[0] || "";
    return `B${num}`;
  }
  return player.name?.[0]?.toUpperCase() || "?";
}

export const PLAYER_COLORS = [
  { hex: "#F59E0B", bg: "rgba(251,191,36,0.18)",   border: "rgba(251,191,36,0.55)",   glow: "rgba(251,191,36,0.22)"  },
  { hex: "#38BDF8", bg: "rgba(56,189,248,0.18)",   border: "rgba(56,189,248,0.55)",   glow: "rgba(56,189,248,0.22)"  },
  { hex: "#FB7185", bg: "rgba(251,113,133,0.18)",  border: "rgba(251,113,133,0.55)",  glow: "rgba(251,113,133,0.22)" },
  { hex: "#A78BFA", bg: "rgba(167,139,250,0.18)",  border: "rgba(167,139,250,0.55)",  glow: "rgba(167,139,250,0.22)" },
];

const STORAGE_PREFIX = "rami-player-colors:";

/**
 * Assigns a stable color to each player for the entire game session.
 * Colors are persisted in localStorage under the roomId key so they survive
 * page refreshes and remain consistent across all rounds of the same game.
 *
 * Returns a map of { user_id → PLAYER_COLORS[i] }.
 */
export function buildPlayerColorMap(players, roomId) {
  const key = roomId ? `${STORAGE_PREFIX}${roomId}` : null;

  // Try to restore a previously saved assignment for this room
  if (key) {
    try {
      const stored = JSON.parse(localStorage.getItem(key) || "null");
      if (stored && players.every((p) => stored[p.user_id] !== undefined)) {
        return Object.fromEntries(
          Object.entries(stored).map(([uid, idx]) => [uid, PLAYER_COLORS[idx]])
        );
      }
    } catch {
      // Corrupt storage — fall through to reassign
    }
  }

  // First time: assign by position (order is stable across rounds in the backend)
  const indexMap = {};
  players.forEach((p, i) => {
    indexMap[p.user_id] = i % PLAYER_COLORS.length;
  });

  if (key) {
    try {
      localStorage.setItem(key, JSON.stringify(indexMap));
    } catch {
      // localStorage full or unavailable — not critical
    }
  }

  return Object.fromEntries(
    Object.entries(indexMap).map(([uid, idx]) => [uid, PLAYER_COLORS[idx]])
  );
}
